import React from 'react'
import { Tooltip } from '@surecompanies/core_components'
import {
  CopyOutlined,
  CreditCardOutlined,
  RollbackOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import { CurrencyFormatter } from 'helpers/formatters'

import { ReviewReason } from '../../../api/reconciliation-v2-enums'
import type { ReviewTransaction } from '../../../api/reconciliation-v2-api-client'
import { AdjustPaymentTarget } from '../AdjustPaymentModal'
import {
  isReturnableSource,
  SOURCE_LABEL,
} from '../AdjustPaymentModal.constants'
import {
  DASH,
  daysSinceTransaction,
  employeeNameOf,
  insuredNameOf,
  REASON_ACTION_LABEL,
  REASON_BADGE_LABEL,
  REASON_TOOLTIP,
  TRANSACTION_GRID_TEMPLATE,
} from './helpers'
import { buildTransactionTarget, transactionToken } from './row-targets'
import BenefitIdCell from './BenefitIdCell'
import { SourceReportedText } from './SourceReported'
import CopyIconButton from './CopyIconButton'
import CopyRowLinkButton from './CopyRowLinkButton'
import JumpToBenefitButton from './JumpToBenefitButton'
import OpenInAppEngineLink from './OpenInAppEngineLink'
import styles from './ReviewQueue.module.scss'

const currencyFormatter = new CurrencyFormatter()

// Provider-pill modifier picker: Marqeta purple, WEX blue, other sources
// neutral. Mirrors the closed set of modifier classes in the module.
const PILL_MODIFIER: Record<string, string> = {
  marqeta: styles.marqeta,
  wex: styles.wex,
}

interface TransactionRowProps {
  row: ReviewTransaction
  canWrite: boolean
  onOpenTarget: (target: AdjustPaymentTarget) => void
}

/**
 * One unposted-transaction row. Cell order must match TRANSACTION_COLUMNS in
 * helpers.ts: Amount, Transaction Number, Transaction ID, Type, Source,
 * Employee, Insured, Carrier, Benefit ID, Token, Date, Days since
 * transaction, Action.
 */
const TransactionRow: React.FC<TransactionRowProps> = ({
  row,
  canWrite,
  onOpenTarget,
}) => {
  const isRefund = row.reason === ReviewReason.Refund
  const pillModifier = PILL_MODIFIER[row.source] ?? styles.neutral
  const pillLabel = (SOURCE_LABEL[row.source] ?? row.source).toUpperCase()
  const token = transactionToken(row)
  const ageInDays = daysSinceTransaction(row.transactionDate)

  // Built by the same helper an `rqOpen` deep link goes through, so a row
  // opened from a shared link and a row opened from this button cannot drift.
  const target = buildTransactionTarget(row)

  // What the source record says this row belongs to — shown, flagged, only
  // while we have nothing of our own. Gated on the benefit here as well as in
  // EPS: a resolved row names OUR benefit, whatever the file says. Display
  // only, and deliberately kept away from `target` and the Action controls.
  const sourceReported = row.benefitId === null ? row.sourceReported : null

  return (
    <div
      className={styles.row}
      style={{ gridTemplateColumns: TRANSACTION_GRID_TEMPLATE }}
    >
      <span className={`${styles.cell} ${styles.num} ${styles.amount}`}>
        {currencyFormatter.safeFormat(String(row.amount), '$0.00')}
      </span>

      {/* The two identifiers, side by side and each labelled, which is the
          whole point: the ledger number is the one to quote, the UUID the one
          the system matches on. The pre-EMS-10248 table showed the UUID under
          a "Reference" header where it was indistinguishable from a card
          token — naming both is what ended that. */}
      <span className={styles.cell}>
        <span className={styles.refLine}>
          <span className={styles.truncate}>{row.transactionNumber}</span>
          <CopyIconButton
            value={row.transactionNumber}
            label="Copy transaction number"
            tooltip="Copy"
            icon={<CopyOutlined />}
          />
        </span>
      </span>

      <span className={styles.cell}>
        <span className={styles.refLine}>
          <span className={styles.truncate}>{row.transactionId}</span>
          <CopyIconButton
            value={row.transactionId}
            label="Copy transaction ID"
            tooltip="Copy"
            icon={<CopyOutlined />}
          />
        </span>
      </span>

      <span className={styles.cell}>
        <Tooltip title={REASON_TOOLTIP[row.reason]}>
          <span className={styles.reasonLabel}>
            {/* Decorative — the label beside it already names the reason. */}
            {isRefund ? (
              <RollbackOutlined aria-hidden />
            ) : (
              <CreditCardOutlined aria-hidden />
            )}
            <span className={styles.truncate}>
              {REASON_BADGE_LABEL[row.reason]}
            </span>
          </span>
        </Tooltip>
      </span>

      <span className={styles.cell}>
        <span className={`${styles.pill} ${pillModifier}`}>{pillLabel}</span>
      </span>

      {/* Both render on every row, not only refunds — the pre-split cell
          blanked non-refund rows even though the names were on the wire. Two
          cells since EMS-10248: neither falls back to the other, so an
          unresolved transaction shows a dash under Insured rather than
          borrowing the employee's name. */}
      <span className={styles.cell}>
        {sourceReported ? (
          <SourceReportedText value={sourceReported.employeeName} />
        ) : (
          <span className={styles.fullText}>{employeeNameOf(row) ?? DASH}</span>
        )}
      </span>

      <span className={styles.cell}>
        {sourceReported ? (
          <SourceReportedText value={sourceReported.insuredName} />
        ) : (
          <span className={styles.fullText}>{insuredNameOf(row) ?? DASH}</span>
        )}
      </span>

      <span className={styles.cell}>
        <span className={styles.truncate}>{row.carrierName ?? DASH}</span>
      </span>

      {sourceReported ? (
        <BenefitIdCell benefitId={sourceReported.benefitId} sourceReported />
      ) : (
        <BenefitIdCell benefitId={row.benefitId} />
      )}

      {/* Only the card rails carry a token; the rest show a bare dash with no
          copy button beside it, there being nothing to copy. */}
      <span className={styles.cell}>
        {token === null ? (
          DASH
        ) : (
          <span className={styles.refLine}>
            <span className={styles.truncate}>{token}</span>
            <CopyIconButton
              value={token}
              label="Copy token"
              tooltip="Copy"
              icon={<CopyOutlined />}
            />
          </span>
        )}
      </span>

      <span className={`${styles.cell} ${styles.dateCell}`}>
        {dayjs(row.transactionDate).format('MMM DD, YYYY h:mm A')}
      </span>

      {/* Bare integer — the header carries the unit, and a "d" suffix on every
          row would just be noise down the column. */}
      <span className={`${styles.cell} ${styles.num}`}>
        {ageInDays ?? DASH}
      </span>

      <span className={`${styles.cell} ${styles.actionCell}`}>
        <button
          className={styles.actionButton}
          type="button"
          disabled={!canWrite}
          onClick={() => onOpenTarget(target)}
        >
          <SwapOutlined aria-hidden />
          {REASON_ACTION_LABEL[row.reason]}
        </button>
        {/* iStream only — opens the same Adjust Payment modal on its Return
            section, where the operator enters the return note and submits. */}
        {isReturnableSource(row.source) && (
          <button
            className={`${styles.actionButton} ${styles.returnButton}`}
            type="button"
            disabled={!canWrite}
            onClick={() => onOpenTarget(buildTransactionTarget(row, 'return'))}
          >
            Return
          </button>
        )}
        {/* Both conditional on the same thing: a transaction the allocator
            could not match to a benefit has nothing to jump to and no
            application to open. `ReviewTransaction.benefitId` is nullable here
            — unlike a discrepancy's, which always resolved — so this arm is
            where the guard has to exist. */}
        {row.benefitId && (
          <>
            <JumpToBenefitButton
              benefitId={row.benefitId}
              date={row.transactionDate}
            />
            <OpenInAppEngineLink benefitId={row.benefitId} />
          </>
        )}
        <CopyRowLinkButton
          target={{ kind: 'transaction', transactionId: row.transactionId }}
        />
      </span>
    </div>
  )
}

export default TransactionRow
